import { useConfirmation, useDeleteConfirmation, useFileViewer, useSection, useServices, useToast } from '@/app/providers';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';
import { DataTable, SearchableSelect, type ColumnDef } from '@/core/ui/components';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import { useCrudNotifications, usePaginatedList } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Role, User, UserRole } from '@/shared/types';
import type { QueryParams } from '@yawi/domain';
import { Ban, CheckCircle, Eye, EyeOff, Key, Layers, Plus, Save, Shield, Trash2, User as UserIcon, UserCog, X, type LucideIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

interface EditingUserState extends Partial<User> {
  password?: string;
  confirmPassword?: string;
}

// Staff roles - excludes RESIDENT and FAMILIAR
const STAFF_ROLES = [
  { value: 'ADMIN', label: 'Administrador', icon: Shield, color: 'purple' },
  // Sin esta entrada, `getRoleInfo` no encontraba SECTION_ADMIN y caía al
  // fallback `allRolesList[2]` (STAFF): un admin de sección se mostraba como
  // personal. El ADMIN lo asigna aquí o en Secciones; un admin de sección solo
  // lo da de alta (editarlo o retirarlo es del ADMIN).
  { value: 'SECTION_ADMIN', label: 'Admin de sección', icon: Layers, color: 'green' },
  { value: 'GUARD', label: 'Guardia de Seguridad', icon: Key, color: 'amber' },
  { value: 'STAFF', label: 'Personal', icon: UserCog, color: 'blue' },
] as const;

interface StaffRoleOption {
  value: string;
  label: string;
  icon: LucideIcon;
  color: 'purple' | 'amber' | 'blue' | 'green' | 'red';
  id?: string;
  isSystem?: boolean;
}

// Helper para obtener el rol efectivo del usuario
const getEffectiveRole = (user: User): string => {
  if (user.tenantRole) return user.tenantRole;
  if (user.role === 'TENANT_USER' || user.role === UserRole.TENANT_USER) return 'RESIDENT';
  return user.role;
};

// Helper para obtener info del rol
const getRoleInfo = (role: string, allRolesList: StaffRoleOption[]) => {
  return allRolesList.find(r => normalizeRoleValue(r.value) === normalizeRoleValue(role)) || allRolesList[2];
};

// Colapsa espacios (u otros separadores) a "_" para que un rol como "Jefe de Mantenimiento"
// matchee sin importar si el backend guarda el código con espacios, guiones o guion bajo.
const normalizeRoleValue = (role: string | undefined) => String(role || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
const BASE_ROLE_VALUES = new Set(['ADMIN', 'SECTION_ADMIN', 'RESIDENT', 'GUARD', 'FAMILIAR', 'STAFF']);

const getUserEndpointRolePayload = (roleValue: string) => {
  const normalizedRole = normalizeRoleValue(roleValue);

  if (normalizedRole === 'STAFF') {
    return {
      role: 'TENANT_USER',
      tenantRole: 'STAFF',
    };
  }

  if (BASE_ROLE_VALUES.has(normalizedRole)) {
    return {
      role: normalizedRole,
      tenantRole: normalizedRole,
    };
  }

  // Roles personalizados (custom roles de staff): `role` vive en MasterUser
  // (identidad global entre tenants) y no tiene un valor para roles custom,
  // así que va TENANT_USER; el rol real (para permisos) es `tenantRole`, que
  // sí es un concepto por-tenant. Usar 'RESIDENT' aca hacia que el backend
  // exigiera unitIds.
  // IMPORTANTE: acá se manda el nombre TAL CUAL (sin uppercase) porque el
  // backend guarda el Role.name exactamente como se creó (con mayúsculas/
  // minúsculas y espacios originales, ej. "Jefe de Mantenimiento"). Si se
  // manda en mayúsculas, tenantRole deja de matchear ese Role.name y el
  // usuario queda sin un rol custom resuelto (se ve como "Personal").
  return {
    role: 'TENANT_USER',
    tenantRole: roleValue.trim(),
  };
};

// Phone formatting helpers
const scrubPhoneToDigits = (val: string) => val.replace(/\D/g, '').slice(0, 10);
const formatPhoneForDisplay = (digits: string) => {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6, 10)}`;
};

export const AdminStaffList = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { confirm } = useConfirmation();
  const { userRepo, roleRepo, sectionRepo, startImpersonation, currentUser } = useServices();
  // Un admin de sección da de alta personal de su sección, nunca admins (§25.11).
  const actsAsSectionAdmin = isSectionAdmin(currentUser);
  const { hasSections, refreshSections } = useSection();
  const ability = useAbility();
  const { openFileByUrl } = useFileViewer();
  const navigate = useNavigate();
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Miembro del equipo',
  });

  const [customRoles, setCustomRoles] = useState<Role[]>([]);
  const canUseStaffAction = useCallback((action: 'create' | 'update' | 'delete') => (
    ability.can(action, 'StaffMenu')
  ), [ability]);
  const canCreateStaff = canUseStaffAction('create');
  const canUpdateStaff = canUseStaffAction('update');
  const canDeleteStaff = canUseStaffAction('delete');
  // "Ver como" es un permiso propio, independiente de poder editar personal.
  const canImpersonate = ability.can('read', 'Impersonation');

  const allRoles = useMemo<StaffRoleOption[]>(() => {
    const rolesByName = new Map(customRoles.map(role => [normalizeRoleValue(role.name), role]));
    const systemRoles = STAFF_ROLES.map(role => ({
      ...role,
      id: rolesByName.get(role.value)?.id,
      isSystem: rolesByName.get(role.value)?.isSystem,
    }));
    const hardcodedValues = new Set(STAFF_ROLES.map(role => normalizeRoleValue(role.value)));
    const customStaffRoles = customRoles
      .filter(role => !['RESIDENT', 'FAMILIAR'].includes(normalizeRoleValue(role.name)))
      .filter(role => !hardcodedValues.has(normalizeRoleValue(role.name)))
      .map(role => ({
        value: role.name,
        label: role.displayName || role.name,
        icon: UserCog,
        color: role.name === 'SUPER_ADMIN' ? 'red' as const : 'green' as const,
        id: role.id,
        isSystem: role.isSystem,
      }));

    return [...systemRoles, ...customStaffRoles];
  }, [customRoles]);

  // `allRoles` sirve para RESOLVER el rol de una fila (incluye SECTION_ADMIN);
  // `assignableRoles` es lo que se puede ELEGIR al crear o editar. Solo el
  // admin global asigna ADMIN; SECTION_ADMIN (si hay secciones) lo asigna el
  // admin global y lo da de alta el admin de sección (no al editar, ver render).
  const assignableRoles = useMemo(
    () => allRoles.filter((role) => {
      const value = normalizeRoleValue(role.value);
      if (value === 'SECTION_ADMIN') return hasSections;
      return !(actsAsSectionAdmin && value === 'ADMIN');
    }),
    [allRoles, actsAsSectionAdmin, hasSections],
  );

  const getSelectedRoleOption = (roleValue: string | undefined) => {
    return allRoles.find(role => normalizeRoleValue(role.value) === normalizeRoleValue(roleValue));
  };

  const handleRoleChange = (roleValue: string) => {
    if (!canUpdateStaff && !canCreateStaff) return;
    setEditingUser({
      ...editingUser,
      role: roleValue as UserRole,
    });
  };

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const roles = await roleRepo.getRoles();
        setCustomRoles(roles);
      } catch (error) {
        console.error('[AdminStaffList] Error loading roles:', error);
      }
    };

    loadRoles();
  }, [roleRepo]);

  // Paginated list - filter staff roles (non-residents)
  const {
    data: users,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh,
  } = usePaginatedList<User>({
    fetchFn: (params: QueryParams) => userRepo.getUsers({ ...params, excludeRoles: 'RESIDENT,FAMILIAR' }),
    errorTitle: 'Error al cargar equipo',
  });

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<EditingUserState>({});
  const isSectionAdminRole = normalizeRoleValue(String(editingUser.role || '')) === 'SECTION_ADMIN';
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Fase 4: secciones a las que está acotado el guardia/staff. Conjunto vacío
  // = ve todo el residencial, que es el estado de todos los guardias hoy.
  const [memberSectionIds, setMemberSectionIds] = useState<string[]>([]);
  const [savedMemberSectionIds, setSavedMemberSectionIds] = useState<string[]>([]);

  const handleOpenCreate = () => {
    if (!canCreateStaff) return;
    const defaultRole = UserRole.GUARD;
    setEditingUser({
      role: defaultRole,
      status: 'ACTIVE',
    });
    setMemberSectionIds([]);
    setSavedMemberSectionIds([]);
    setIsEditMode(false);
    setIsModalOpen(true);
    refreshSections();
  };

  const handleOpenEdit = (user: User) => {
    if (!canUpdateStaff) return;
    const effectiveRole = getEffectiveRole(user);
    setEditingUser({
      ...user,
      role: effectiveRole as UserRole,
    });
    setMemberSectionIds([]);
    setSavedMemberSectionIds([]);
    setIsEditMode(true);
    setIsModalOpen(true);
    refreshSections();

    // Las secciones del miembro no vienen en el listado de usuarios; se piden
    // al abrir el modal. Si falla, el multi-select queda vacío y guardar sin
    // tocarlo no cambia nada (se compara contra lo guardado).
    if (hasSections && user.memberId) {
      void sectionRepo
        .getMemberSections(user.memberId)
        .then(({ sectionIds }) => {
          setMemberSectionIds(sectionIds);
          setSavedMemberSectionIds(sectionIds);
        })
        .catch(() => undefined);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canDeleteStaff) return;
    await confirmDelete({
      itemName: name,
      itemType: 'Miembro del equipo',
      onConfirm: async () => {
        await withDeleteNotification(() => userRepo.deleteUser(id));
        refresh();
      }
    });
  };

  const handleViewAs = async (user: User) => {
    try {
      await startImpersonation(user.id);
      navigate('/');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al iniciar la vista previa');
    }
  };

  const handleToggleBlock = async (user: User) => {
    if (!canUpdateStaff) return;
    const newStatus = user.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
    const action = newStatus === 'BLOCKED' ? 'Bloquear' : 'Desbloquear';
    const confirmed = await confirm({
      title: `${action} personal`,
      message: newStatus === 'BLOCKED'
        ? `${user.name} no podrá acceder al sistema mientras esté bloqueado. ¿Deseas continuar?`
        : `${user.name} podrá volver a acceder al sistema. ¿Deseas continuar?`,
      confirmText: action,
      variant: newStatus === 'BLOCKED' ? 'warning' : 'info',
    });
    if (!confirmed) return;

    // Endpoints dedicados (no el PUT genérico): blockUser revoca tokens y
    // unblockUser reactiva la membresía del tenant, sin lo cual el usuario
    // queda con status ACTIVE pero sigue sin poder iniciar sesión.
    await withUpdateNotification(() => (
      newStatus === 'BLOCKED' ? userRepo.blockUser(user.id) : userRepo.unblockUser(user.id)
    ));
    refresh();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode ? !canUpdateStaff : !canCreateStaff) return;
    if (!editingUser.name || !editingUser.email) return;

    if (!isEditMode) {
      const pwd = editingUser.password || '';
      if (pwd.length < 8) {
        toast.error('La contraseña debe tener al menos 8 caracteres');
        return;
      }
      if (!/[A-Z]/.test(pwd)) {
        toast.error('La contraseña debe tener al menos una mayúscula');
        return;
      }
      if (!/[a-z]/.test(pwd)) {
        toast.error('La contraseña debe tener al menos una minúscula');
        return;
      }
      if (!/[0-9]/.test(pwd)) {
        toast.error('La contraseña debe tener al menos un número');
        return;
      }
      if (!/[^A-Za-z0-9]/.test(pwd)) {
        toast.error('La contraseña debe tener al menos un carácter especial');
        return;
      }
      if (pwd !== editingUser.confirmPassword) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
    }

    // Retirar todas sus secciones lo degrada a residente en el backend; para
    // eso está "Retirar" en Secciones. Al crear, puede quedar sin sección.
    if (isSectionAdminRole && isEditMode && savedMemberSectionIds.length > 0 && memberSectionIds.length === 0) {
      toast.error('Un admin de sección debe conservar al menos una sección');
      return;
    }

    try {
      const selectedRole = getSelectedRoleOption(String(editingUser.role || ''));
      const roleValue = selectedRole?.value || String(editingUser.role || UserRole.GUARD);
      const rolePayload = getUserEndpointRolePayload(roleValue);

      const payload: Record<string, unknown> = {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        status: editingUser.status,
        ...rolePayload,
      };

      if (!isEditMode) {
        payload.password = editingUser.password;
        // El admin de sección elige entre sus secciones (la activa va marcada).
        if (isSectionAdminRole || actsAsSectionAdmin) payload.sectionIds = memberSectionIds;
      }

      // Scrub phone to digits only before sending
      if (payload.phone) {
        payload.phone = scrubPhoneToDigits(payload.phone as string);
      }

      if (isEditMode && editingUser.id) {
        delete payload.password;

        await withUpdateNotification(() => userRepo.updateUser({ id: editingUser.id, ...payload } as unknown as User));
      } else {
        await withCreateNotification(() => userRepo.createUser(payload as unknown as Omit<User, 'id'>));
      }
      // Las secciones son una tabla aparte (`member_sections`): solo se escriben
      // si el admin las tocó, para no borrar asignaciones al guardar otros campos.
      const sectionsChanged =
        [...memberSectionIds].sort().join(',') !== [...savedMemberSectionIds].sort().join(',');
      if (isEditMode && editingUser.memberId && sectionsChanged) {
        const memberId = editingUser.memberId;
        if (isSectionAdminRole) {
          // Las secciones de un admin de sección se asignan una a una (el
          // backend rechaza `setMemberSections` para este rol). Primero se
          // suman, para no dejarlo nunca sin sección al retirar.
          for (const id of memberSectionIds.filter(id => !savedMemberSectionIds.includes(id))) {
            await sectionRepo.assignSectionAdmin(id, memberId);
          }
          for (const id of savedMemberSectionIds.filter(id => !memberSectionIds.includes(id))) {
            await sectionRepo.removeSectionAdmin(id, memberId);
          }
        } else {
          await sectionRepo.setMemberSections(memberId, memberSectionIds);
        }
      }

      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  // Column definitions
  const columns: ColumnDef<User>[] = useMemo(() => [
    {
      id: 'name',
      header: t('admin.users.table.name'),
      cell: (user) => (
        <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-slate-500 ${user.status === 'BLOCKED' ? 'bg-red-100' : 'bg-slate-200'}`}>
              <UserIcon size={14} />
            </div>
          <div>
            <p className={`font-medium ${user.status === 'BLOCKED' ? 'text-red-800 line-through' : 'text-slate-900'}`}>{user.name}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      header: t('admin.users.table.role'),
      cell: (user) => {
        const effectiveRole = getEffectiveRole(user);
        const roleInfo = getRoleInfo(effectiveRole, allRoles);
        const IconComponent = roleInfo.icon;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
            roleInfo.color === 'purple'
              ? 'bg-purple-50 text-purple-700 border-purple-200'
              : roleInfo.color === 'amber'
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : roleInfo.color === 'green'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-blue-50 text-blue-700 border-blue-200'
          }`}>
            <IconComponent size={10} />
            {roleInfo.label}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: t('admin.users.table.status'),
      className: 'text-center',
      cell: (user) => (
        user.status === 'ACTIVE' ? (
          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
            <CheckCircle size={10} /> {t('admin.users.status.active')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-600 text-xs font-bold bg-red-50 px-2 py-1 rounded-full border border-red-100">
            <Ban size={10} /> {t('admin.users.status.blocked')}
          </span>
        )
      ),
    },
  ], [t, allRoles]);

  const canSaveModal = isEditMode ? canUpdateStaff : canCreateStaff;

  return (
    <>
      <DataTable
        data={users}
        columns={columns}
        keyExtractor={(user) => user.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.card.staff')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre..."
        loading={loading}
        emptyMessage="No hay miembros del equipo."
        actions={canCreateStaff ? (
          <Button onClick={handleOpenCreate} icon={<Plus size={16}/>}>
            {t('admin.staff.btn.create')}
          </Button>
        ) : undefined}
        rowActions={(canImpersonate || canUpdateStaff || canDeleteStaff) ? (user) => {
          // Editar, bloquear o eliminar a un admin (también de sección) es del ADMIN.
          const managesRow = !(actsAsSectionAdmin && ['ADMIN', 'SECTION_ADMIN', 'SUPER_ADMIN'].includes(getEffectiveRole(user)));
          return (
          <>
            {canImpersonate && getEffectiveRole(user) !== 'ADMIN' && getEffectiveRole(user) !== 'SUPER_ADMIN' && (
              <button
                onClick={() => handleViewAs(user)}
                className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                title={t('admin.users.viewAs')}
              >
                <Eye size={16} />
              </button>
            )}
            {canUpdateStaff && managesRow && (
              <>
                <button
                  onClick={() => handleToggleBlock(user)}
                  className={`p-1.5 rounded transition-colors ${user.status === 'ACTIVE' ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                  title={user.status === 'ACTIVE' ? t('admin.users.modal.block') : t('admin.users.modal.unblock')}
                >
                  {user.status === 'ACTIVE' ? <Ban size={16}/> : <CheckCircle size={16}/>}
                </button>
                <button
                  onClick={() => handleOpenEdit(user)}
                  className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  title={t('common.edit')}
                >
                  <UserIcon size={16} />
                </button>
              </>
            )}
            {canDeleteStaff && managesRow && (
              <button
                onClick={() => handleDelete(user.id, user.name)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title={t('common.delete')}
              >
                <Trash2 size={16} />
              </button>
            )}
          </>
          );
        } : undefined}
      />

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar personal' : 'Nuevo Miembro del Equipo'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20}/>
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto">
              <fieldset disabled={!canSaveModal} className="space-y-4">
              <div>
                <SearchableSelect
                  label={t('common.role')}
                  // SECTION_ADMIN exige elegir abajo las secciones que administra:
                  // sin filas en `member_sections` no vería nada.
                  options={assignableRoles
                    .filter(role => !(actsAsSectionAdmin && isEditMode && normalizeRoleValue(role.value) === 'SECTION_ADMIN'))
                    .map(role => ({ value: role.value, label: role.label }))}
                  value={editingUser.role ?? null}
                  onChange={value => handleRoleChange(value ?? '')}
                  isDisabled={!canSaveModal}
                  placeholder={t('common.role')}
                />
              </div>
              <Input
                label={t('admin.users.table.name')}
                value={editingUser.name || ''}
                onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('common.email')}
                  type="email"
                  value={editingUser.email || ''}
                  onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                  required
                />
                <Input
                  label={t('common.phone')}
                  value={formatPhoneForDisplay(editingUser.phone || '')}
                  onChange={e => setEditingUser({...editingUser, phone: scrubPhoneToDigits(e.target.value)})}
                />
              </div>

              {/* El admin de sección asigna secciones solo en el alta: cambiarlas
                  después (`PUT /sections/members`) es del ADMIN. */}
              {hasSections && (isEditMode ? !!editingUser.memberId && !actsAsSectionAdmin : isSectionAdminRole || actsAsSectionAdmin) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {isSectionAdminRole ? 'Secciones que administra' : 'Secciones visibles'}
                  </label>
                  <SectionMultiSelect
                    value={memberSectionIds}
                    onChange={setMemberSectionIds}
                    resourceLabel="acceso a todo el residencial"
                    emptyHint={isSectionAdminRole ? 'Sin secciones: no verá datos hasta que se le asigne una.' : undefined}
                    idPrefix="staff"
                    isDisabled={!canSaveModal}
                  />
                </div>
              )}

              {/* Password Fields - ONLY WHEN CREATING */}
              {!isEditMode && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative flex items-center">
                    <Input
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      value={editingUser.password || ''}
                      onChange={e => setEditingUser({...editingUser, password: e.target.value})}
                      placeholder="Mínimo 8 caracteres"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors mt-6"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <Input
                      label="Confirmar Contraseña"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={editingUser.confirmPassword || ''}
                      onChange={e => setEditingUser({...editingUser, confirmPassword: e.target.value})}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors mt-6"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}
              </fieldset>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                {canSaveModal && (
                  <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                    {t('common.save')}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
